import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import Cookies from 'universal-cookie'
import { COOKIE_NAME, SALESFORCE_COOKIE } from '../../const/cookie'
import {
  validatePaidMembership,
  hasMemberBookPrice,
  hasVoteEligibilty,
  hasELSubscription,
  hasActivateSubscription,
  hasPaywallMembership,
  hasELBadge,
  hasDigitalAds,
} from '../../lib/access-validator'
import {
  hasPaidMembershipVar,
  hasMemberBookPriceVar,
  hasVoteEligibiltyVar,
  hasELSubscriptionVar,
  hasActivateSubscriptionVar,
  hasPaywallMembershipVar,
  hasELBadgeVar,
  hasDigitalAdsVar,
} from '../../lib/apollo-client/cache'
import fetchSalesforceAccessByEmail from '../../lib/fetches/fetchSalesforceAccessByEmail'
import { hostnameForCookie } from '../utils'

const cookies = new Cookies()

/**
 * Manages the user account data retrieval and updating of related user account information.
 * This hook is triggered by a reactive variable being updated.
 *
 * @returns {Object}
 */
const useUserAccount = () => {
  const [user, setUser] = useState(null)
  const [salesforceAccess, setSalesforceAccess] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        let sid = cookies.get(SALESFORCE_COOKIE)
        if (sid) {
          const response = await fetch(
            `${process.env.NEXT_PUBLIC_SALESFORCE_BACKEND_URL_TEST}?sid=${sid}`,
            {
              // Adding cache control headers to prevent caching
              cache: 'no-store',
              headers: {
                'Cache-Control': 'no-store',
              },
            }
          )
          if (response.ok) {
            const userData = await response.json()
            setUser(userData)
            // Store user data in session storage
            sessionStorage.setItem('user', JSON.stringify(userData))

            // Set Sentry user context
            if (userData.authenticated && userData.user) {
              Sentry.setUser({
                id: userData.user.salesforceId,
                email: userData.user.email,
                username: userData.user.displayName || userData.user.email,
              })
            } else {
              // Clear Sentry user context when not authenticated
              Sentry.setUser(null)
            }

            if (userData.authenticated) {
              const sfAccess = await fetchSalesforceAccessByEmail(
                userData?.user?.email
              )

              setSalesforceAccess(sfAccess)

              let existingCookie = cookies.get(COOKIE_NAME)
              const isPaidMember = validatePaidMembership(sfAccess?.access)
              if (isPaidMember !== hasPaidMembershipVar()) {
                hasPaidMembershipVar(isPaidMember)
              }

              const isPaywallMember = hasPaywallMembership(sfAccess?.access)
              if (isPaywallMember !== hasPaywallMembershipVar()) {
                hasPaywallMembershipVar(isPaywallMember)
              }

              const isELBadgeMember = hasELBadge(sfAccess?.user)
              if (isELBadgeMember !== hasELBadgeVar()) {
                hasELBadgeVar(isELBadgeMember)
              }

              const isDigitalAds = hasDigitalAds(sfAccess?.access)
              if (isDigitalAds !== hasDigitalAdsVar()) {
                hasDigitalAdsVar(isDigitalAds)
              }

              const isMembeBookPrice = hasMemberBookPrice(sfAccess?.access)
              if (isMembeBookPrice !== hasMemberBookPriceVar()) {
                hasMemberBookPriceVar(isMembeBookPrice)
              }

              const isActivateSubscriber = hasActivateSubscription(
                sfAccess?.user
              )
              if (isActivateSubscriber !== hasActivateSubscriptionVar()) {
                hasActivateSubscriptionVar(isActivateSubscriber)
              }

              //update cookie for paywall check
              if (existingCookie) {
                existingCookie['isPaywallMember'] = isPaywallMember
                existingCookie['isActivateSubscriber'] = isActivateSubscriber
                existingCookie['isELBadgeMember'] = isELBadgeMember
                //retrieve the expires value from the existing cookie and set it to the new cookie
                //default value is current time + 1 hour
                let expiresValue = Math.floor(Date.now() / 1000) + 3600
                for (let key in existingCookie) {
                  if (existingCookie[key].expires) {
                    expiresValue = existingCookie[key].expires
                    break
                  }
                }
                cookies.set(COOKIE_NAME, existingCookie, {
                  path: '/',
                  domain: hostnameForCookie,
                  expires: new Date(expiresValue * 1000),
                })
              } else {
                cookies.set(
                  COOKIE_NAME,
                  { isPaywallMember, isActivateSubscriber },
                  {
                    path: '/',
                    domain: hostnameForCookie,
                    expires: new Date(new Date().valueOf() + 3600000),
                    sameSite: 'none',
                    secure: true,
                  }
                )
              }
              const isELSubscriber = hasELSubscription(sfAccess?.access)
              if (isELSubscriber !== hasELSubscriptionVar()) {
                hasELSubscriptionVar(isELSubscriber)
              }

              const isVoteEligible = hasVoteEligibilty(
                sfAccess?.access
                  ? sfAccess?.access.filter((i) => i.user_access.granted)
                  : sfAccess?.access
              )
              if (isVoteEligible !== hasVoteEligibiltyVar()) {
                hasVoteEligibiltyVar(isVoteEligible)
              }
            } else {
              cookies.remove(COOKIE_NAME, {
                path: '/',
                domain: hostnameForCookie,
              })
            }
          } else {
            console.error('Error fetching user profile:', response.statusText)
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchUserProfile()
  }, [])

  return {
    loading,
    userAccountUser: {
      masterCustomerId: salesforceAccess?.user?.id,
      salesforceAccess,
    },
    userAccountAccess: salesforceAccess?.access,
    user,
  }
}

export default useUserAccount
